import i18next, { type Resource } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, isLocale, LOCALE_TAGS, LOCALES, type Locale } from '@kts/shared';
import common_uz from './locales/uz/common.json';
import common_ru from './locales/ru/common.json';
import common_en from './locales/en/common.json';
import common_zh from './locales/zh/common.json';

/**
 * Keytosell tillar tizimi (i18next ustida).
 *
 * Qoidalar:
 *  - birlamchi va zaxira til — o'zbekcha: tarjima topilmasa o'zbekcha matn chiqadi,
 *    hech qachon kalit nomi yoki bo'sh joy ko'rinmaydi;
 *  - har modul o'z nom maydoniga (namespace) ega: `orders`, `wms`, `finance`…;
 *  - `common` — tugmalar, holatlar, tasdiqlar kabi butun dastur bo'ylab takrorlanadigan matnlar.
 */

const STORAGE_KEY = 'kts.locale';

/**
 * Foydalanuvchi shu qurilmada ATAYLAB tanlagan til (tanlamagan bo'lsa — null).
 * Brauzer tilidan farqlash uchun kerak: server bilan sinxronlashda tanlov ustun turadi.
 */
export function storedLocale(): Locale | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isLocale(saved) ? saved : null;
  } catch {
    return null; // maxfiy oyna
  }
}

/** Tanlangan til → brauzer tili → o'zbekcha. */
export function detectLocale(): Locale {
  const saved = storedLocale();
  if (saved) return saved;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = (tag ?? '').split('-')[0]?.toLowerCase();
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

function markHtmlLang(locale: Locale) {
  document.documentElement.lang = LOCALE_TAGS[locale];
}

/**
 * Ilova ishga tushganda bir marta chaqiriladi. `resources` — ilovaning o'z lug'atlari
 * (modul nom maydonlari). `common` avtomatik qo'shiladi.
 */
export function initI18n(resources: Resource = {}): typeof i18next {
  const merged: Resource = {};
  for (const locale of LOCALES) {
    merged[locale] = { common: COMMON[locale], ...(resources[locale] ?? {}) };
  }
  const locale = detectLocale();
  void i18next.use(initReactI18next).init({
    resources: merged,
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    // Tarjima topilmasa kalit emas, o'zbekcha matn chiqsin
    returnEmptyString: false,
    interpolation: { escapeValue: false },
  });
  markHtmlLang(locale);
  return i18next;
}

const COMMON = { uz: common_uz, ru: common_ru, en: common_en, zh: common_zh };

/** Joriy til. */
export function currentLocale(): Locale {
  const l = i18next.resolvedLanguage ?? i18next.language;
  return isLocale(l) ? l : DEFAULT_LOCALE;
}

/**
 * Tilni almashtirish. `remember: false` bo'lsa (serverdan kelgan til qo'llanayotganda)
 * qurilmadagi tanlov yozilmaydi — foydalanuvchi tanlovi o'chib ketmasin.
 */
export async function setLocale(locale: Locale, remember = true): Promise<void> {
  await i18next.changeLanguage(locale);
  if (remember) {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* e'tiborsiz */
    }
  }
  markHtmlLang(locale);
}

/**
 * Tarjima hook'i: `const t = useT('orders')` → `t('title')`.
 * Ikkinchi qaytariladigan qiymat — joriy til (statuslar, sanalar uchun).
 */
export function useT(ns?: string) {
  const { t } = useTranslation(ns);
  return t;
}

/** Joriy tilni kuzatuvchi hook (til almashganda qayta chiziladi). */
export function useLocale(): Locale {
  const { i18n } = useTranslation();
  const l = i18n.resolvedLanguage ?? i18n.language;
  return isLocale(l) ? l : DEFAULT_LOCALE;
}

export { i18next };
export type { Resource };

/**
 * Matn ichida teg bo'lsa (`"<em>ombor</em> va ..."`) shu komponent ishlatiladi:
 * `<Trans t={t} i18nKey="headline" components={{ em: <em /> }} />`.
 * Tarjimonlar so'z tartibini erkin o'zgartira oladi, teg o'z joyida qoladi.
 */
export { Trans } from 'react-i18next';
